import React, { useEffect, useState } from 'react';
import { Modal } from '../ui/Modal';
import { createManualThread } from '../../api/emailThreads';
import { apiClient } from '../../api/client';

type Kind = 'phone' | 'meeting' | 'note';

interface Props {
  isOpen: boolean;
  mandateId: string;
  companyId: string;
  onClose: () => void;
  onSaved?: () => void;
}

interface ContactOption {
  id: string;
  display_name: string;
}

export function ManualEntryDialog({ isOpen, mandateId, companyId, onClose, onSaved }: Props) {
  const [kind, setKind]         = useState<Kind>('phone');
  const [subject, setSubject]   = useState('');
  const [body, setBody]         = useState('');
  const [occurredAt, setOcc]    = useState(() => toLocalIso(new Date()));
  const [contactId, setContact] = useState<string>('');
  const [contacts, setContacts] = useState<ContactOption[]>([]);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState<string | null>(null);

  // Kontakte der Firma laden
  useEffect(() => {
    if (!isOpen) return;
    setKind('phone'); setSubject(''); setBody(''); setContact('');
    setOcc(toLocalIso(new Date())); setError(null);
    void apiClient.get(`/companies/${companyId}`).then(resp => {
      const list = (resp.data?.data?.contacts ?? []) as ContactOption[];
      setContacts(list);
    }).catch(() => setContacts([]));
  }, [isOpen, companyId]);

  const handleSave = async () => {
    if (!subject.trim()) { setError('Bitte einen Betreff angeben.'); return; }
    setSaving(true); setError(null);
    try {
      await createManualThread({
        mandate_id: mandateId,
        contact_id: contactId || undefined,
        kind,
        subject: subject.trim(),
        body_text: body,
        occurred_at: new Date(occurredAt).toISOString(),
      });
      onSaved?.();
      onClose();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } }).response?.data?.error;
      setError(msg ?? 'Fehler beim Speichern.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Kommunikation manuell eintragen" size="md">
      <div style={{ display: 'grid', gap: '12px' }}>
        <div>
          <label style={labelStyle}>Art</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            {(['phone', 'meeting', 'note'] as const).map(k => (
              <button key={k} onClick={() => setKind(k)}
                style={{
                  padding: '7px 14px', fontSize: '12px', borderRadius: 'var(--radius)',
                  border: '1px solid ' + (kind === k ? 'var(--accent)' : 'var(--border2)'),
                  background: kind === k ? 'var(--accent-light)' : 'var(--surface)',
                  color: kind === k ? 'var(--accent)' : 'var(--text2)',
                  cursor: 'pointer', fontWeight: kind === k ? 600 : 400,
                }}>
                {KIND_LABELS[k]}
              </button>
            ))}
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
          <div>
            <label style={labelStyle}>Datum / Uhrzeit</label>
            <input type="datetime-local" value={occurredAt}
              onChange={e => setOcc(e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Ansprechpartner (optional)</label>
            <select value={contactId} onChange={e => setContact(e.target.value)} style={inputStyle}>
              <option value="">— ohne Person —</option>
              {contacts.map(c => <option key={c.id} value={c.id}>{c.display_name}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label style={labelStyle}>Betreff / Kurzbeschreibung</label>
          <input value={subject} onChange={e => setSubject(e.target.value)}
            placeholder={KIND_PLACEHOLDERS[kind]} style={inputStyle} />
        </div>

        <div>
          <label style={labelStyle}>Notiz / Ergebnis (optional)</label>
          <textarea value={body} onChange={e => setBody(e.target.value)}
            rows={8}
            placeholder="Was wurde besprochen? Welches Ergebnis / nächste Schritte?"
            style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.55 }} />
        </div>

        {error && (
          <div style={{ padding: '8px 12px', background: 'var(--accent-light)', color: 'var(--accent)', borderRadius: 'var(--radius)', fontSize: '12px' }}>
            ⚠️ {error}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '4px' }}>
          <button onClick={onClose} disabled={saving}
            style={{ padding: '8px 16px', background: 'var(--surface)', border: '1px solid var(--border2)',
              borderRadius: 'var(--radius)', fontSize: '13px', cursor: saving ? 'not-allowed' : 'pointer', color: 'var(--text2)' }}>
            Abbrechen
          </button>
          <button onClick={handleSave} disabled={saving || !subject.trim()}
            style={{
              padding: '9px 22px', fontSize: '13px', fontWeight: 600,
              background: 'var(--accent)', color: '#fff', border: 'none',
              borderRadius: 'var(--radius)', cursor: saving ? 'wait' : 'pointer',
              opacity: saving ? 0.7 : 1,
            }}>
            {saving ? '⏳ Speichern …' : '✓ Eintragen'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

const KIND_LABELS: Record<Kind, string> = {
  phone: '📞 Telefonat',
  meeting: '🤝 Treffen',
  note: '📝 Notiz',
};
const KIND_PLACEHOLDERS: Record<Kind, string> = {
  phone:   'z. B. „Erstkontakt zu Hotel Resort Frankfurt"',
  meeting: 'z. B. „Site-Visit Berliner Str. mit J. Rubin"',
  note:    'z. B. „Brief gesendet, wartet auf Rückmeldung"',
};

function toLocalIso(d: Date): string {
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const labelStyle: React.CSSProperties = {
  display: 'block', fontSize: '11px', fontWeight: 500,
  textTransform: 'uppercase', letterSpacing: '0.5px',
  color: 'var(--text3)', marginBottom: '5px',
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: '13px',
  border: '1px solid var(--border2)', borderRadius: 'var(--radius)',
  background: 'var(--surface)', color: 'var(--text)',
};
